// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { Flex, QRCode, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAuthorizeDeviceHandoffMutation,
  useCreateDeviceHandoffMutation,
  useGetDeviceHandoffStatusQuery,
  useGetGrantStatusQuery,
  useGetPatientQuery,
  useGetPhotoQuery,
  usePostGrantMutation,
} from "../../api/hooks";
import DemographicsCard from "../../components/demographics/DemographicsCard";
import { Button, HexagonSpinner, Snackbar } from '@imprivata-cloud/patient-access-ui-components';
import { usePlatform } from "../../context/platform/hooks";
import "./DeviceHandoffComponent.less";
import { ErrorComponent } from "../../components/error/ErrorComponent";
import { HandoffTokenStatus } from "../../models/handoff-token-status";
import Photo from "../photo/Photo";
import { navigateWithContext } from '@utils/NavigationHelper';
import { useTranslation } from 'react-i18next';

const ProcessingSpinner: React.FC = () => {
  // Hook must be inside component scope
  const { t } = useTranslation();
  return (
    <>
      <Typography.Title level={2} className="text-center" data-testid="device-handoff-processing-title">
        {t('deviceHandoffComponent.were-processing-your-information')}
      </Typography.Title>
      <HexagonSpinner data-testid="loading-spinner" />
    </>
  );
};
const DeviceHandoffComponent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { featureFlags } = usePlatform();
  const [handoffUri, setHandoffUri] = useState<string | null>(null);
  const [handoffStatus, setHandoffStatus] = useState<HandoffTokenStatus | null>(
    null
  );
  const handoffInitiatedRef = useRef(false);
  const { grantStatus, enable: enableGrantStatusQuery } =
    useGetGrantStatusQuery(false);
  const { postGrant } = usePostGrantMutation({});
  const {
    patient,
    isLoading: isPatientLoading,
    error: patientError,
    enable: enablePatientQuery,
  } = useGetPatientQuery(false);
  const {
    tokenStatus,
    isSuccessfulVerification,
    error: tokenStatusError,
    disable,
    setToken,
    getToken,
  } = useGetDeviceHandoffStatusQuery();
  const {
    authorizeDeviceHandoff,
    isPending: isAuthorizing,
    isSuccess: authorizeSuccess,
    error: authorizeError,
  } = useAuthorizeDeviceHandoffMutation({
    getHandoffId: getToken,
    onSuccess: () => {
      enableGrantStatusQuery();
    },
  });
  const { initiateDeviceHandoff, apiError: initiateDeviceHandoffError } =
    useCreateDeviceHandoffMutation({
      onSuccess: (response) => {
        setHandoffStatus(response?.tokenStatus);
        if (response?.handoffUri) {
          setHandoffUri(response.handoffUri);
        }
        if (response?.token) {
          setToken(response.token);
        }
      },
    });
  const { photo } = useGetPhotoQuery(patient?.photoUrl);
  const isHandoffCompleted = handoffStatus && handoffStatus === "Completed";
  const isHandlingAuthorization =
    authorizeSuccess || isAuthorizing || isHandoffCompleted;
  useEffect(() => {
    if (!handoffInitiatedRef.current) {
      handoffInitiatedRef.current = true;
      initiateDeviceHandoff();
    }
    return () => {
      disable();
    };
  }, []);
  useEffect(() => {
    if (tokenStatus && handoffStatus !== tokenStatus) {
      setHandoffStatus(tokenStatus);
    }
  }, [tokenStatus, handoffStatus]);
  useEffect(() => {
    if (isHandoffCompleted) {
      disable();
      if (!(isAuthorizing || authorizeSuccess || authorizeError)) {
        authorizeDeviceHandoff();
      }
    }
  }, [handoffStatus, isAuthorizing, authorizeSuccess, authorizeError]);
  useEffect(() => {
    if (authorizeSuccess) {
      // Always try to enable patient query first - if patient is authenticated,
      // it will succeed and show demographics. If not, it will fail and we'll call postGrant
      enablePatientQuery();
    }
  }, [authorizeSuccess]);
  // Handle patient query error - if patient is not authenticated, call postGrant
  useEffect(() => {
    if (patientError && authorizeSuccess) {
      postGrant();
    }
  }, [patientError, authorizeSuccess]);
  if (initiateDeviceHandoffError) {
    return <ErrorComponent cause={initiateDeviceHandoffError.error.code} />;
  }
  if (tokenStatusError) {
    return <ErrorComponent cause="missing-handoff-token" />;
  }
  const handlePrimaryClick = () => {
    navigateWithContext(navigate, '/consent');
  };
  if (patient) {
    return (
      <Flex gap={30} vertical align="center" className="w-100">
        <Photo image={photo ?? undefined} />
        <DemographicsCard
          givenName={patient.givenName}
          surname={patient.surname}
          dateOfBirth={patient.dateOfBirth}
          mrn={patient.mrn}
          isLoading={isPatientLoading}
        />
        <Snackbar
          variant="success"
          title={
            isSuccessfulVerification
              ? t('deviceHandoffComponent.successful-verification')
              : t('deviceHandoffComponent.successful-enrollment')
          }
          className="w-100 align-self-center"
        />
        <Button
          variant="primary"
          label={t('deviceHandoffComponent.continue')}
          data-testid="enroll-continue-btn"
          onClick={handlePrimaryClick}
          disabled={!grantStatus?.pendingAuthorization}
          className="align-self-center w-100 max-width-button"
          autoFocus
        />
      </Flex>
    );
  }
  // Show loading when authorization is in progress or patient is not available
  if (isHandlingAuthorization) {
    return <ProcessingSpinner />;
  }
  if (handoffUri && handoffStatus === HandoffTokenStatus.Started) {
    return (
      <>
        <HexagonSpinner data-testid="loading-spinner" />
        <Typography.Title
          level={2}
          className="text-center"
          data-testid="account-device-handoff-started-title"
        >{t('deviceHandoffComponent.continue-on-your-mobile-device')}</Typography.Title>
        <Typography.Paragraph
          className="text-center w-auto"
          data-testid="account-device-handoff-started-text"
        >
          {t('deviceHandoffComponent.successful-scanned-qr-continue-mobile')}
        </Typography.Paragraph>
      </>
    );
  }
  if (handoffUri && handoffStatus === HandoffTokenStatus.Pending) {
    return (
      <>
        <Typography.Title
          level={2}
          className="text-center account-device-handoff-title mt-16"
          data-testid="account-device-handoff-title"
          >{t('deviceHandoffComponent.need-phone-complete-account-creation')}</Typography.Title>
        <Typography.Title
          level={3}
          className="text-center account-device-handoff-instructions"
          data-testid="account-device-handoff-instructions"
          >{t('deviceHandoffComponent.scan-qr-begin-mobile')}</Typography.Title>
        <Flex
          className="account-device-handoff-flex-container w-100"
          gap={16}
          align="center"
          justify="center"
          vertical
        >
          <QRCode
            value={handoffUri}
            size={300}
            type="svg"
            data-testid="account-device-handoff-qrcode"
          />
          {featureFlags.DEVICE_HANDOFF_LINK_ENABLED && (
            <a href={handoffUri} target="_blank" rel="noopener noreferrer">
              {t('deviceHandoffComponent.handoff-link')}
            </a>
          )}
        </Flex>
      </>
    );
  }
  return (
    <>
      <Typography.Title
        level={2}
        className="text-center"
        data-testid="account-device-handoff-loading-title"
        >{t('deviceHandoffComponent.preparing-next-step-verification')}</Typography.Title>
      <HexagonSpinner data-testid="loading-spinner" />
    </>
  );
};
export default DeviceHandoffComponent;
